import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Users, Target, TrendingUp, Calendar, Activity, Brain, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import ConsultantRequestButton from '../components/ConsultantRequestButton';

const AthleteOverview = () => {
  const { user } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [athleteData, setAthleteData] = useState({
    summary: null,
    trainingPlans: [],
    performanceMetrics: [],
    goals: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAthletes();
  }, []);

  useEffect(() => {
    if (selectedAthlete) {
      fetchAthleteData(selectedAthlete.id);
    }
  }, [selectedAthlete]);

  const fetchAthletes = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coach/athletes`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAthletes(data.athletes);
        if (data.athletes.length > 0) {
          setSelectedAthlete(data.athletes[0]);
        }
      } else {
        console.error('Failed to fetch athletes');
      }
    } catch (error) {
      console.error('Error fetching athletes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAthleteData = async (athleteId) => {
    setLoading(true);
    try {
      // Fetch summary
      const summaryResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coach/athlete/${athleteId}/summary`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Fetch training plans
      const plansResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coach/athlete/${athleteId}/training-plans`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Fetch performance metrics
      const metricsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coach/athlete/${athleteId}/performance-metrics?days=30`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Fetch goals
      const goalsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coach/athlete/${athleteId}/goals`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const [summaryData, plansData, metricsData, goalsData] = await Promise.all([
        summaryResponse.ok ? summaryResponse.json() : null,
        plansResponse.ok ? plansResponse.json() : [],
        metricsResponse.ok ? metricsResponse.json() : [],
        goalsResponse.ok ? goalsResponse.json() : []
      ]);

      setAthleteData({
        summary: summaryData,
        trainingPlans: plansData,
        performanceMetrics: metricsData,
        goals: goalsData
      });

    } catch (error) {
      console.error('Error fetching athlete data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGoalStatusColor = (progress) => {
    if (progress >= 100) return 'bg-green-100 text-green-800';
    if (progress >= 75) return 'bg-blue-100 text-blue-800';
    if (progress >= 50) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getGoalStatusIcon = (progress) => {
    if (progress >= 100) return <CheckCircle className="w-4 h-4" />;
    if (progress >= 75) return <TrendingUp className="w-4 h-4" />;
    return <Clock className="w-4 h-4" />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (!user || !['coach', 'sport-performance-consultant', 'team-admin', 'system-admin', 'school-admin'].includes(user.role)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 p-6">
        <div className="max-w-4xl mx-auto text-center py-12">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">Only coaches and administrators can access athlete overview data.</p>
        </div>
      </div>
    );
  }

  if (loading && athletes.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading athlete data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Athlete Overview</h1>
              <p className="text-gray-600">Monitor athlete progress, goals, and performance metrics</p>
            </div>
            <div>
              <ConsultantRequestButton user={user} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Athletes List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Users className="w-5 h-5" />
                  <span>My Athletes ({athletes.length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {athletes.map((athlete) => (
                    <button
                      key={athlete.id}
                      onClick={() => setSelectedAthlete(athlete)}
                      className={`w-full text-left p-4 hover:bg-gray-50 transition-colors border-b last:border-b-0 ${
                        selectedAthlete?.id === athlete.id ? 'bg-emerald-50 border-l-4 border-l-emerald-500' : ''
                      }`}
                    >
                      <div className="font-medium text-gray-900">{athlete.name}</div>
                      <div className="text-sm text-gray-500">{athlete.sport || 'General'}</div>
                      <div className="text-xs text-gray-400">
                        {athlete.teams?.map(team => team.team_name).join(', ')}
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Athlete Details */}
          <div className="lg:col-span-3">
            {selectedAthlete ? (
              <>
                {/* Athlete Header */}
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-xl">{selectedAthlete.name}</CardTitle>
                    <CardDescription>
                      {selectedAthlete.sport || 'General'} • {selectedAthlete.email}
                    </CardDescription>
                  </CardHeader>
                </Card>

                {/* Summary Stats */}
                {athleteData.summary && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Avg Mood (7 days)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-blue-600">
                          {athleteData.summary.summary_stats?.avg_mood_7days || 0}/10
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Avg Stress (7 days)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-orange-600">
                          {athleteData.summary.summary_stats?.avg_stress_7days || 0}/10
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Active Goals</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-emerald-600">
                          {athleteData.summary.summary_stats?.active_goals_count || 0}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Detailed Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="performance">Performance</TabsTrigger>
                    <TabsTrigger value="goals">Goals</TabsTrigger>
                    <TabsTrigger value="training">Training Plans</TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview" className="mt-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Recent Performance Chart */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center space-x-2">
                            <TrendingUp className="w-5 h-5" />
                            <span>Recent Performance Trends</span>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {athleteData.performanceMetrics.length > 0 ? (
                            <ResponsiveContainer width="100%" height={250}>
                              <LineChart data={athleteData.performanceMetrics.slice(0, 10).reverse()}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" tickFormatter={(date) => new Date(date).toLocaleDateString()} />
                                <YAxis domain={[0, 10]} />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="mood" stroke="#3B82F6" name="Mood" strokeWidth={2} />
                                <Line type="monotone" dataKey="confidence" stroke="#10B981" name="Confidence" strokeWidth={2} />
                                <Line type="monotone" dataKey="stress_level" stroke="#F59E0B" name="Stress" strokeWidth={2} />
                              </LineChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="text-center py-8 text-gray-500">
                              No performance data available
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Recent Goals */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center space-x-2">
                            <Target className="w-5 h-5" />
                            <span>Active Goals</span>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            {athleteData.goals.filter(goal => goal.progress < 100).slice(0, 5).map((goal, index) => (
                              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex-1">
                                  <div className="font-medium text-sm">{goal.title}</div>
                                  <div className="text-xs text-gray-500">Due: {formatDate(goal.target_date)}</div>
                                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                    <div 
                                      className="bg-emerald-500 h-2 rounded-full" 
                                      style={{ width: `${goal.progress}%` }}
                                    ></div>
                                  </div>
                                </div>
                                <div className="ml-4 flex items-center space-x-2">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getGoalStatusColor(goal.progress)}`}>
                                    {goal.progress}%
                                  </span>
                                </div>
                              </div>
                            ))}
                            {athleteData.goals.filter(goal => goal.progress < 100).length === 0 && (
                              <div className="text-center py-4 text-gray-500">
                                No active goals
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </TabsContent>

                  <TabsContent value="performance" className="mt-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Performance Metrics (Last 30 Days)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {athleteData.performanceMetrics.length > 0 ? (
                          <div className="space-y-6">
                            <ResponsiveContainer width="100%" height={300}>
                              <LineChart data={athleteData.performanceMetrics.reverse()}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" tickFormatter={(date) => new Date(date).toLocaleDateString()} />
                                <YAxis domain={[0, 10]} />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="mood" stroke="#3B82F6" name="Mood" strokeWidth={2} />
                                <Line type="monotone" dataKey="confidence" stroke="#10B981" name="Confidence" strokeWidth={2} />
                                <Line type="monotone" dataKey="stress_level" stroke="#F59E0B" name="Stress Level" strokeWidth={2} />
                                <Line type="monotone" dataKey="energy_level" stroke="#8B5CF6" name="Energy" strokeWidth={2} />
                              </LineChart>
                            </ResponsiveContainer>

                            {/* Recent Entries Table */}
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b">
                                    <th className="text-left py-2">Date</th>
                                    <th className="text-left py-2">Mood</th>
                                    <th className="text-left py-2">Confidence</th>
                                    <th className="text-left py-2">Stress</th>
                                    <th className="text-left py-2">Energy</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {athleteData.performanceMetrics.slice(0, 10).map((metric, index) => (
                                    <tr key={index} className="border-b">
                                      <td className="py-2">{formatDate(metric.date)}</td>
                                      <td className="py-2">{metric.mood || '-'}/10</td>
                                      <td className="py-2">{metric.confidence || '-'}/10</td>
                                      <td className="py-2">{metric.stress_level || '-'}/10</td>
                                      <td className="py-2">{metric.energy_level || '-'}/10</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <Activity className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                            <p>No performance metrics recorded yet</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="goals" className="mt-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {athleteData.goals.map((goal, index) => (
                        <Card key={index}>
                          <CardHeader>
                            <CardTitle className="flex items-center justify-between">
                              <span className="flex items-center space-x-2">
                                {getGoalStatusIcon(goal.progress)}
                                <span className="text-lg">{goal.title}</span>
                              </span>
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getGoalStatusColor(goal.progress)}`}>
                                {goal.progress}%
                              </span>
                            </CardTitle>
                            <CardDescription>
                              Due: {formatDate(goal.target_date)} • {goal.category}
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <p className="text-gray-600 mb-4">{goal.description}</p>
                            <div className="w-full bg-gray-200 rounded-full h-3">
                              <div 
                                className="bg-emerald-500 h-3 rounded-full transition-all duration-300" 
                                style={{ width: `${goal.progress}%` }}
                              ></div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      {athleteData.goals.length === 0 && (
                        <div className="col-span-2 text-center py-8 text-gray-500">
                          <Target className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                          <p>No goals set yet</p>
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="training" className="mt-6">
                    <div className="space-y-4">
                      {athleteData.trainingPlans.map((plan, index) => (
                        <Card key={index}>
                          <CardHeader>
                            <CardTitle className="flex items-center space-x-2">
                              <Brain className="w-5 h-5" />
                              <span>{plan.title}</span>
                            </CardTitle>
                            <CardDescription>
                              Created: {formatDate(plan.created_at)} • {plan.sport} • {plan.weeks} weeks
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <p className="text-gray-600">{plan.plan_content}</p>
                          </CardContent>
                        </Card>
                      ))}
                      {athleteData.trainingPlans.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                          <p>No training plans created yet</p>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </>
            ) : (
              <Card>
                <CardContent className="text-center py-12">
                  <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Select an athlete to view their data</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AthleteOverview;